import { Box, useTheme, useMediaQuery } from '@mui/material';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AppHeader from './AppHeader';
import Sidebar from './Sidebar';
import AgentChatPanel from './AgentChatPanel';
import AccountSetupBanner from './AccountSetupBanner';
import { useAuth } from '../contexts/AuthContext';
import { useAgentContext } from '../contexts/AgentContext';
import { loadDateLocaleFromSystem } from '../services/dateLocaleService';

interface AppLayoutProps {
  children: React.ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile); // Open by default on desktop, closed on mobile
  const { user } = useAuth();
  const { setPageContext } = useAgentContext();

  const handleMenuToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Update sidebar state when screen size changes
  // This is a valid pattern for responsive UI - the effect only runs when isMobile changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  // Load locale metadata (country + IANA) for country-aware features (for example holiday calendars/date blocking).
  useEffect(() => {
    void loadDateLocaleFromSystem();
  }, []);

  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const pageId = segments.length > 0 ? segments[0] : 'home';
    setPageContext({
      pageId,
      pageTitle: pageId.replace(/-/g, ' '),
      route: location.pathname,
    });
  }, [location.pathname, setPageContext]);

  // Check if user is admin or manager (including TeamLead)
  const userRoles = user?.roles || [];
  const isAdmin = userRoles.includes('Administrator');
  const isManager = userRoles.some((role: string) => 
    role === 'Manager' || role === 'SeniorManager' || role === 'Administrator' || role === 'TeamLead'
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: '#f5f7fa',
        '@media print': { bgcolor: '#fff', minHeight: 'auto' },
      }}
    >
      <Box sx={{ '@media print': { display: 'none' } }}>
        <AppHeader onMenuToggle={handleMenuToggle} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          mt: '64px',
          '@media print': { mt: 0 },
        }}
      >
        <Box sx={{ '@media print': { display: 'none' } }}>
          <Sidebar open={sidebarOpen} onToggle={handleMenuToggle} isAdmin={isAdmin} isManager={isManager} />
        </Box>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { md: `calc(100% - ${sidebarOpen ? 240 : 64}px)` },
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            '@media print': {
              width: '100% !important',
              maxWidth: '100%',
              p: 2,
            },
          }}
        >
          <AccountSetupBanner />
          {children}
        </Box>
      </Box>
      <AgentChatPanel />
    </Box>
  );
}
